import { useCallback, useEffect, useRef, useState } from 'react'
import type { SyntheticEvent } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'

import { config } from '@/config'
import { useUser } from '@/hooks/use-user'

const HEIGHT_VAR = '--BetterStackBanner-height'
const DISMISS_COOKIE = 'betteruptime_announcement_bar_dismissed'
const POLL_INTERVAL_MS = 5 * 60 * 1000

interface Announcement {
	html: string
	digest: string
	link: string | null
}

function isDismissed(digest: string): boolean {
	return document.cookie.split(';').some((c) => c.trim() === `${DISMISS_COOKIE}=${digest}`)
}

function writeDismissCookie(digest: string) {
	document.cookie = `${DISMISS_COOKIE}=${digest};path=/`
}

function parseMessageHtml(wrapperHtml: string): string {
	const doc = new DOMParser().parseFromString(wrapperHtml, 'text/html')
	return doc.querySelector('.betteruptime-announcement__message')?.innerHTML.trim() ?? ''
}

export function BetterStackStatus() {
	const widgetId = config.betterstack?.widgetId
	const { user, isLoading } = useUser()
	const [announcement, setAnnouncement] = useState<Announcement | null>(null)
	const [dismissedDigest, setDismissedDigest] = useState<string | null>(null)
	const bannerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!widgetId || isLoading || !user) return
		let cancelled = false

		const fetchAnnouncement = async () => {
			try {
				const response = await fetch(`https://uptime.betterstack.com/widgets/announcement?id=${widgetId}`)
				const data = await response.json()
				if (cancelled) return
				const html = data.hidden || !data.html ? '' : parseMessageHtml(data.html)
				setAnnouncement(html ? { html, digest: String(data.digest ?? ''), link: data.link ?? null } : null)
			} catch {
				if (!cancelled) setAnnouncement(null)
			}
		}

		fetchAnnouncement()
		const interval = setInterval(fetchAnnouncement, POLL_INTERVAL_MS)
		return () => {
			cancelled = true
			clearInterval(interval)
		}
	}, [widgetId, user, isLoading])

	const visible = Boolean(user && announcement && announcement.digest !== dismissedDigest && !isDismissed(announcement.digest))

	useEffect(() => {
		const update = () => {
			const height = visible && bannerRef.current ? `${bannerRef.current.offsetHeight}px` : '0px'
			document.documentElement.style.setProperty(HEIGHT_VAR, height)
		}
		update()
		window.addEventListener('resize', update)
		return () => {
			window.removeEventListener('resize', update)
			document.documentElement.style.removeProperty(HEIGHT_VAR)
		}
	}, [visible, announcement])

	const handleDismiss = useCallback(
		(event: SyntheticEvent) => {
			event.stopPropagation()
			if (!announcement) return
			writeDismissCookie(announcement.digest)
			setDismissedDigest(announcement.digest)
		},
		[announcement],
	)

	const handleOpenLink = useCallback(() => {
		if (announcement?.link) window.open(announcement.link, '_blank')
	}, [announcement])

	if (!visible || !announcement) return null

	return (
		<Box
			ref={bannerRef}
			sx={{
				position: 'fixed',
				top: 'var(--ImpersonationBanner-height, 0px)',
				left: 0,
				right: 0,
				zIndex: 1090,
			}}
		>
			<Alert
				severity="warning"
				onClose={handleDismiss}
				onClick={announcement.link ? handleOpenLink : undefined}
				sx={{
					borderRadius: 0,
					cursor: announcement.link ? 'pointer' : undefined,
					justifyContent: 'center',
					'& .MuiAlert-message': {
						flex: 1,
						textAlign: { xs: 'left', sm: 'center' },
						'& p': {
							m: 0,
							display: { xs: '-webkit-box', sm: 'block' },
							WebkitLineClamp: { xs: 2, sm: 'unset' },
							WebkitBoxOrient: 'vertical',
							overflow: 'hidden',
						},
					},
				}}
			>
				{/* eslint-disable-next-line react/no-danger -- announcement text authored in our own Better Stack status page */}
				<span dangerouslySetInnerHTML={{ __html: announcement.html }} />
			</Alert>
		</Box>
	)
}
